import Phaser from 'phaser';
import { GameParams } from '../params';
import { HUD_AVATAR_KEY } from '../systems/avatar';
import { AUDIO_KEYS, playBgm } from '../systems/audio';
import { TUTORIAL_SEEN_KEY } from './TutorialScene';

export class IntroScene extends Phaser.Scene {
  private params!: GameParams;

  constructor() {
    super('Intro');
  }

  init(data: { params: GameParams }) {
    this.params = data.params;
  }

  create() {
    const overlay = document.getElementById('intro-overlay')!;
    const nameEl = document.getElementById('intro-name')!;
    const avatarEl = document.getElementById('intro-avatar') as HTMLImageElement;

    nameEl.textContent = this.params.name || 'Agent';

    // Only override the HTML default (player_body.png — the hero icon shown
    // inside the circle in the reference UI) when the user actually supplied
    // an image via the URL params. Without that, the composited HUD avatar
    // is just the procedural smiley fallback, which doesn't match the design.
    if (this.params.imageUrl) {
      try {
        const tex = this.textures.get(HUD_AVATAR_KEY);
        const src = tex.getSourceImage() as HTMLCanvasElement | HTMLImageElement;
        if (src instanceof HTMLCanvasElement) {
          avatarEl.src = src.toDataURL();
        } else if (src instanceof HTMLImageElement && src.src) {
          avatarEl.src = src.src;
        }
      } catch {
        // keep default player_body.png set in HTML
      }
    }

    // Restart intro BGM here so music plays on replay too (BootScene only
    // runs on initial page load). Idempotent: a no-op if bgmIntro is already
    // playing. Falls back to bgmMain if the intro track isn't loaded.
    const introKey = this.cache.audio.exists(AUDIO_KEYS.bgmIntro)
      ? AUDIO_KEYS.bgmIntro
      : AUDIO_KEYS.bgmMain;
    playBgm(this, introKey);

    overlay.classList.remove('hidden');
    void overlay.offsetHeight; // force reflow so animations start from FROM state
    overlay.classList.add('ix-animate');

    // Use an AbortController to clean up DOM listeners on scene shutdown.
    // Without this, if a listener never fires (e.g. user clicks DEPLOY so
    // the overlay-pointerdown one is skipped), it leaks across replays and
    // can fire from a defunct scene when the user replays.
    const ac = new AbortController();
    const begin = () => {
      overlay.classList.remove('ix-animate');
      overlay.classList.add('hidden');
      // First play → tutorial. Replay (or subsequent runs in the same tab)
      // → straight to game. The flag is set by TutorialScene on BEGIN.
      let seen = false;
      try { seen = sessionStorage.getItem(TUTORIAL_SEEN_KEY) === '1'; } catch { /* sessionStorage unavailable */ }
      const next = seen ? 'Game' : 'Tutorial';
      this.scene.start(next, { params: this.params });
    };

    const deployBtn = document.getElementById('intro-deploy-btn');
    deployBtn?.addEventListener('click', (e) => { e.stopPropagation(); begin(); }, { once: true, signal: ac.signal });

    overlay.addEventListener('pointerdown', (e) => {
      if ((e.target as HTMLElement).closest('#intro-deploy-btn')) return;
      begin();
    }, { once: true, signal: ac.signal });

    this.input.keyboard?.once('keydown', begin);

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => ac.abort());
  }
}
